<template>
    <div class="bill-management-container container">
      <el-card shadow="hover">
        <template #header>
          <div class="card-header">
            <h2>إدارة فواتير المشتريات والمصروفات</h2>
            <el-button type="primary" @click="handleCreate">
              <el-icon><Plus /></el-icon> تسجيل فاتورة جديدة
            </el-button>
          </div>
        </template>
  
        <el-alert
          title="ما هي هذه الواجهة؟"
          type="info"
          description="هذه الواجهة مخصصة لتسجيل ومتابعة جميع الفواتير والمصروفات التي تتلقاها المدرسة من الموردين. يمكنك من هنا تسجيل فاتورة جديدة، عرض تفاصيلها، وتسجيل الدفعات التي تتم لسدادها."
          :closable="false"
          show-icon
          style="margin-bottom: 20px;"
        />
  
        <el-table :data="bills" v-loading="loading" border style="width: 100%">
          <el-table-column prop="bill_number" label="رقم الفاتورة" />
          <el-table-column prop="vendor.name" label="المورد" />
          <el-table-column prop="issue_date" label="تاريخ الإصدار" />
          <el-table-column prop="total_amount" label="المبلغ الإجمالي" />
          <el-table-column prop="paid_amount" label="المبلغ المدفوع" />
          <el-table-column prop="remaining_amount" label="المبلغ المتبقي" />
          <el-table-column label="الحالة">
              <template #default="{ row }">
                  <el-tag :type="getStatusTagType(row.status)">{{ row.status }}</el-tag>
              </template>
          </el-table-column>
          <el-table-column label="الإجراءات" width="220" fixed="right">
            <template #default="{ row }">
              <el-button size="small" @click="handleView(row)">عرض</el-button>
              <el-button size="small" type="primary" @click="openPaymentDialog(row)" :disabled="row.status === 'paid'">
                  <el-icon><Money /></el-icon> تسجيل دفعة
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        
        <div class="pagination-container">
          <el-pagination v-if="pagination.total > 0" @current-change="handlePageChange" :current-page="pagination.current_page" :page-size="pagination.per_page" layout="total, prev, pager, next" :total="pagination.total" background />
        </div>
      </el-card>
  
      <!-- Dialog لإنشاء فاتورة -->
      <el-dialog v-model="createDialogVisible" title="إضافة فاتورة مشتريات جديدة" width="60%" :close-on-click-modal="false">
        <el-form :model="form" ref="formRef" label-position="top" :rules="rules">
          <el-row :gutter="20">
              <el-col :span="12">
                  <el-form-item label="المورد" prop="vendor_id">
                      <el-select v-model="form.vendor_id" filterable placeholder="اختر المورد" style="width: 100%;">
                          <el-option v-for="vendor in vendors" :key="vendor.id" :label="vendor.name" :value="vendor.id" />
                      </el-select>
                  </el-form-item>
              </el-col>
              <el-col :span="12">
                  <el-form-item label="رقم فاتورة المورد (اختياري)" prop="bill_number">
                      <el-input v-model="form.bill_number" />
                  </el-form-item>
              </el-col>
          </el-row>
          <el-row :gutter="20">
              <el-col :span="12">
                  <el-form-item label="تاريخ الإصدار" prop="issue_date">
                      <el-date-picker v-model="form.issue_date" type="date" style="width: 100%;" />
                  </el-form-item>
              </el-col>
              <el-col :span="12">
                  <el-form-item label="تاريخ الاستحقاق (اختياري)" prop="due_date">
                      <el-date-picker v-model="form.due_date" type="date" style="width: 100%;" />
                  </el-form-item>
              </el-col>
          </el-row>
  
          <el-divider>بنود الفاتورة</el-divider>
          <div v-for="(item, index) in form.items" :key="index" class="bill-item-row">
              <el-select v-model="item.expense_account_id" placeholder="حساب المصروف" style="flex: 2;">
                  <el-option v-for="account in expenseAccounts" :key="account.id" :label="account.name" :value="account.id" />
              </el-select>
              <el-input v-model="item.description" placeholder="وصف البند" style="flex: 3;" />
              <el-input-number v-model="item.amount" :min="0.01" :precision="2" style="flex: 1;" />
              <el-button type="danger" circle @click="removeItem(index)"><el-icon><Delete /></el-icon></el-button>
          </div>
          <el-button @click="addItem" style="margin-top: 10px;"><el-icon><Plus /></el-icon> إضافة بند</el-button>
          <div class="total-amount">الإجمالي: {{ totalAmount.toFixed(2) }} دينار</div>
        </el-form>
        <template #footer>
          <span class="dialog-footer">
            <el-button @click="createDialogVisible = false">إلغاء</el-button>
            <el-button type="primary" @click="submitForm" :loading="formLoading">حفظ الفاتورة</el-button>
          </span>
        </template>
      </el-dialog>
  
      <!-- Dialog لعرض تفاصيل الفاتورة -->
      <el-dialog v-model="detailsDialogVisible" title="تفاصيل فاتورة المورد" width="70%">
          <div v-if="selectedBill" v-loading="detailsLoading" id="bill-details-printable">
              <el-descriptions :column="2" border>
                  <el-descriptions-item label="رقم الفاتورة">{{ selectedBill.bill_number }}</el-descriptions-item>
                  <el-descriptions-item label="المورد">{{ selectedBill.vendor.name }}</el-descriptions-item>
                  <el-descriptions-item label="تاريخ الإصدار">{{ selectedBill.issue_date }}</el-descriptions-item>
                  <el-descriptions-item label="تاريخ الاستحقاق">{{ selectedBill.due_date }}</el-descriptions-item>
                  <el-descriptions-item label="المبلغ الإجمالي">{{ selectedBill.total_amount }} دينار</el-descriptions-item>
                  <el-descriptions-item label="الحالة">
                      <el-tag :type="getStatusTagType(selectedBill.status)">{{ selectedBill.status }}</el-tag>
                  </el-descriptions-item>
              </el-descriptions>
  
              <el-divider>بنود الفاتورة</el-divider>
              <el-table :data="selectedBill.items" border>
                  <el-table-column prop="description" label="الوصف" />
                  <el-table-column prop="expense_account.name" label="حساب المصروف" />
                  <el-table-column prop="amount" label="المبلغ" />
              </el-table>
  
              <el-divider>الدفعات المسجلة</el-divider>
              <el-table :data="selectedBill.payments" border>
                  <el-table-column prop="payment_date" label="تاريخ الدفعة" />
                  <el-table-column prop="amount" label="المبلغ المدفوع" />
                  <el-table-column prop="payment_method" label="طريقة الدفع" />
                  <el-table-column prop="treasury.name" label="الخزينة" />
              </el-table>
          </div>
          <template #footer>
              <span class="dialog-footer">
                  <el-button @click="printBill"><el-icon><Printer /></el-icon> طباعة</el-button>
                  <el-button @click="detailsDialogVisible = false">إغلاق</el-button>
              </span>
          </template>
      </el-dialog>
  
      <!-- Dialog لتسجيل دفعة جديدة -->
      <el-dialog v-model="paymentDialogVisible" title="تسجيل دفعة جديدة" width="40%" :close-on-click-modal="false">
          <el-form v-if="selectedBill" :model="paymentForm" ref="paymentFormRef" label-position="top">
              <p>المبلغ المتبقي على الفاتورة: <strong>{{ selectedBill.remaining_amount }} دينار</strong></p>
              <el-form-item label="المبلغ المدفوع" prop="amount_paid">
                  <el-input-number v-model="paymentForm.amount_paid" :min="0.01" :max="selectedBill.remaining_amount" :precision="2" style="width: 100%;" />
              </el-form-item>
              <el-form-item label="الخزينة" prop="treasury_id">
                  <el-select v-model="paymentForm.treasury_id" placeholder="اختر الخزينة" style="width: 100%;">
                      <el-option v-for="treasury in treasuries" :key="treasury.id" :label="treasury.name" :value="treasury.id" />
                  </el-select>
              </el-form-item>
               <el-form-item label="تاريخ الدفعة" prop="payment_date">
                  <el-date-picker v-model="paymentForm.payment_date" type="date" placeholder="اختر تاريخ الدفعة" style="width: 100%;" />
              </el-form-item>
          </el-form>
          <template #footer>
              <span class="dialog-footer">
                  <el-button @click="paymentDialogVisible = false">إلغاء</el-button>
                  <el-button type="primary" @click="submitPayment" :loading="isSavingPayment">حفظ الدفعة</el-button>
              </span>
          </template>
      </el-dialog>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import { ElMessage, ElPopconfirm } from 'element-plus';
  import { Plus, Delete, Search, Money, Printer } from '@element-plus/icons-vue';
  
  export default {
    name: 'BillManagement',
    components: { Plus, Delete, Search, Money, Printer, ElPopconfirm },
    data() {
      return {
        api: null,
        loading: false,
        bills: [],
        vendors: [],
        expenseAccounts: [],
        treasuries: [],
        pagination: { current_page: 1, per_page: 15, total: 0 },
        
        createDialogVisible: false,
        formLoading: false,
        form: this.getInitialForm(),
        rules: {
          vendor_id: [{ required: true, message: 'المورد مطلوب', trigger: 'change' }],
          issue_date: [{ required: true, message: 'تاريخ الإصدار مطلوب', trigger: 'change' }],
        },
  
        detailsDialogVisible: false,
        detailsLoading: false,
        selectedBill: null,
  
        paymentDialogVisible: false,
        isSavingPayment: false,
        paymentForm: this.getInitialPaymentForm(),
      };
    },
    computed: {
      dialogTitle() {
        return this.form.id ? 'تعديل فاتورة مشتريات' : 'إضافة فاتورة مشتريات جديدة';
      },
      totalAmount() {
          return this.form.items.reduce((sum, item) => sum + (item.amount || 0), 0);
      }
    },
    created() {
      this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
      this.api.interceptors.request.use((config) => {
        const token = localStorage.getItem('access_token');
        if (token) config.headers['Authorization'] = `Bearer ${token}`;
        return config;
      });
      this.fetchBills();
      this.fetchFormData();
      this.fetchTreasuries();
    },
    methods: {
      getInitialForm() {
        return { 
            id: null, 
            vendor_id: null, 
            bill_number: '',
            issue_date: new Date(),
            due_date: null,
            items: [{ expense_account_id: null, description: '', amount: 0 }] 
        };
      },
      getInitialPaymentForm() {
          return {
              bill_id: null,
              treasury_id: null,
              amount_paid: 0,
              payment_date: new Date(),
              payment_method: 'cash',
          };
      },
      async fetchBills(page = 1) {
        this.loading = true;
        try {
          const response = await this.api.get('/bills', { params: { page, per_page: this.pagination.per_page } });
          this.bills = response.data.data;
          this.pagination.total = response.data.total;
          this.pagination.current_page = response.data.current_page;
        } catch (error) {
          ElMessage.error('فشل في جلب الفواتير.');
        } finally {
          this.loading = false;
        }
      },
      async fetchFormData() {
          try {
              const [vendorsRes, accountsRes] = await Promise.all([
                  this.api.get('/vendors'),
                  this.api.get('/accounts', { params: { type: 'expense' } })
              ]);
              this.vendors = vendorsRes.data;
              this.expenseAccounts = accountsRes.data;
          } catch (error) {
              ElMessage.error('فشل في جلب البيانات اللازمة.');
          }
      },
      async fetchTreasuries() {
        try {
          const response = await this.api.get('/treasuries');
          this.treasuries = response.data;
        } catch (error) {
          ElMessage.error('فشل في جلب الخزائن.');
        }
      },
      handlePageChange(page) {
        this.fetchBills(page);
      },
      handleCreate() {
        this.form = this.getInitialForm();
        this.createDialogVisible = true;
      },
      async handleView(bill) {
          this.detailsDialogVisible = true;
          this.detailsLoading = true;
          try {
              const response = await this.api.get(`/bills/${bill.id}`);
              this.selectedBill = response.data;
          } catch (error) {
              ElMessage.error('فشل في جلب تفاصيل الفاتورة.');
              this.detailsDialogVisible = false;
          } finally {
              this.detailsLoading = false;
          }
      },
      openPaymentDialog(bill) {
        this.selectedBill = bill;
        this.paymentForm = this.getInitialPaymentForm();
        this.paymentForm.bill_id = bill.id;
        this.paymentDialogVisible = true;
      },
      async submitPayment() {
          this.isSavingPayment = true;
          try {
              await this.api.post('/payments/bill', this.paymentForm);
              ElMessage.success('تم تسجيل الدفعة بنجاح!');
              this.paymentDialogVisible = false;
              this.fetchBills(this.pagination.current_page);
          } catch (error) {
              ElMessage.error(error.response?.data?.message || 'فشل في تسجيل الدفعة.');
          } finally {
              this.isSavingPayment = false;
          }
      },
      printBill() {
          if (!this.selectedBill) return;
  
          const logoUrl = `${window.location.origin}/logo-school-one.png`;
          const bill = this.selectedBill;
          
          const itemsHtml = bill.items.map(item => `
              <tr>
                  <td>${item.description}</td>
                  <td>${item.expense_account.name}</td>
                  <td>${parseFloat(item.amount).toFixed(2)} دينار</td>
              </tr>
          `).join('');
  
          const paymentsHtml = bill.payments.map(payment => `
              <tr>
                  <td>${payment.payment_date}</td>
                  <td>${payment.treasury.name}</td>
                  <td>${parseFloat(payment.amount).toFixed(2)} دينار</td>
              </tr>
          `).join('');
  
          const printContent = `
              <html>
              <head>
                  <title>Bill #${bill.bill_number}</title>
                  <style>
                      body { font-family: 'Tajawal', sans-serif; direction: rtl; }
                      .invoice-box { max-width: 800px; margin: auto; padding: 30px; border: 1px solid #eee; box-shadow: 0 0 10px rgba(0, 0, 0, 0.15); font-size: 16px; line-height: 24px; color: #555; }
                      .invoice-box table { width: 100%; line-height: inherit; text-align: right; }
                      .invoice-box table td { padding: 5px; vertical-align: top; }
                      .invoice-box table tr.top table td { padding-bottom: 20px; }
                      .invoice-box table tr.top table td.title { font-size: 45px; line-height: 45px; color: #333; }
                      .invoice-box table tr.information table td { padding-bottom: 40px; }
                      .invoice-box table tr.heading td { background: #eee; border-bottom: 1px solid #ddd; font-weight: bold; }
                      .invoice-box table tr.details td { padding-bottom: 20px; }
                      .invoice-box table tr.item td { border-bottom: 1px solid #eee; }
                      .invoice-box table tr.item.last td { border-bottom: none; }
                      .invoice-box table tr.total td:nth-child(2) { border-top: 2px solid #eee; font-weight: bold; }
                      @media print {
                          body, .invoice-box { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                      }
                  </style>
              </head>
              <body>
                  <div class="invoice-box">
                      <table cellpadding="0" cellspacing="0">
                          <tr class="top">
                              <td colspan="3">
                                  <table>
                                      <tr>
                                          <td class="title"><img src="${logoUrl}" style="width:100%; max-width:150px;"></td>
                                          <td>
                                              فاتورة #: ${bill.bill_number || bill.id}<br>
                                              تاريخ الإصدار: ${bill.issue_date}<br>
                                              تاريخ الاستحقاق: ${bill.due_date}
                                          </td>
                                      </tr>
                                  </table>
                              </td>
                          </tr>
                          <tr class="information">
                              <td colspan="3">
                                  <table>
                                      <tr>
                                          <td>
                                              <strong>فاتورة من:</strong><br>
                                              ${bill.vendor.name}<br>
                                              ${bill.vendor.email || ''}<br>
                                              ${bill.vendor.phone || ''}
                                          </td>
                                      </tr>
                                  </table>
                              </td>
                          </tr>
                          <tr class="heading">
                              <td>الوصف</td>
                              <td>حساب المصروف</td>
                              <td style="text-align: left;">المبلغ</td>
                          </tr>
                          ${itemsHtml}
                          <tr class="total">
                              <td colspan="2"></td>
                              <td style="text-align: left;"><strong>الإجمالي: ${parseFloat(bill.total_amount).toFixed(2)} دينار</strong></td>
                          </tr>
                      </table>
                      <h3 style="margin-top: 40px;">الدفعات المسجلة</h3>
                      <table cellpadding="0" cellspacing="0">
                           <tr class="heading">
                              <td>تاريخ الدفعة</td>
                              <td>الخزينة</td>
                              <td style="text-align: left;">المبلغ</td>
                          </tr>
                          ${paymentsHtml}
                      </table>
                  </div>
              </body>
              </html>
          `;
  
          const printWindow = window.open('', '_blank');
          printWindow.document.write(printContent);
          printWindow.document.close();
          printWindow.focus();
          setTimeout(() => {
              printWindow.print();
              printWindow.close();
          }, 250);
      },
      addItem() {
          this.form.items.push({ expense_account_id: null, description: '', amount: 0 });
      },
      removeItem(index) {
          if (this.form.items.length > 1) {
              this.form.items.splice(index, 1);
          }
      },
      async submitForm() {
        if (!this.$refs.formRef) return;
        await this.$refs.formRef.validate(async (valid) => {
          if (!valid) return;
          this.formLoading = true;
          try {
            await this.api.post('/bills', this.form);
            ElMessage.success('تم إنشاء الفاتورة بنجاح!');
            this.createDialogVisible = false;
            this.fetchBills();
          } catch (error) {
            const errorMsg = error.response?.data?.errors ? Object.values(error.response.data.errors)[0][0] : (error.response?.data?.message || 'فشلت العملية.');
            ElMessage.error(errorMsg);
          } finally {
            this.formLoading = false;
          }
        });
      },
      async handleDelete(id) {
          try {
              await this.api.delete(`/bills/${id}`);
              ElMessage.success('تم حذف الفاتورة بنجاح!');
              this.fetchBills();
          } catch (error) {
              ElMessage.error(error.response?.data?.message || 'فشل في حذف الفاتورة.');
          }
      },
      getStatusTagType(status) {
        const statusMap = {
          paid: 'success',
          partially_paid: 'warning',
          unpaid: 'info',
        };
        return statusMap[status] || 'info';
      },
    },
  };
  </script>
  
  <style scoped>
  .bill-management-container { padding: 20px; }
  .card-header { display: flex; justify-content: space-between; align-items: center; }
  .pagination-container { display: flex; justify-content: center; margin-top: 20px; }
  .bill-item-row { display: flex; gap: 10px; align-items: center; margin-bottom: 15px; }
  .total-amount { text-align: right; font-size: 1.2em; font-weight: bold; margin-top: 20px; }
  </style>
  